import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CheckCircle2, Copy, FlaskConical, PlugZap, Radio, XCircle } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import { api, errorMessage } from '@/lib/api'
import { cn } from '@/lib/cn'
import Badge from '@/components/ui/Badge'
import Button from '@/components/ui/Button'
import Card from '@/components/ui/Card'
import ConfirmDialog from '@/components/ui/ConfirmDialog'
import Modal from '@/components/ui/Modal'
import PageHeader from '@/components/ui/PageHeader'
import { Field, Input } from '@/components/ui/Field'
import { Select } from '@/components/ui/Select'
import { useToast } from '@/components/ui/toast'
import LogoLoader from '@/components/ui/LogoLoader'

interface IntegrationField {
  key: string
  label: string
  type: 'text' | 'secret' | 'bool' | 'select'
  options: string[] | null
  value: string | null
  has_value: boolean
  hint: string | null
}

interface Provider {
  provider: string
  label: string
  blurb: string
  configured: boolean
  fields: IntegrationField[]
}

/* One tab per group, laid out like Printscape's and the event app's
   Integrations: one category at a time instead of a long page of half-empty
   rows. `purpose` fills the card footer for providers that have no mode. */
const GROUPS = [
  { key: 'payments', title: 'Payments', providers: ['billplz', 'chip'], purpose: '' },
  { key: 'shipping', title: 'Shipping', providers: ['poslaju'], purpose: 'Courier booking' },
  { key: 'email', title: 'Email', providers: ['sendscape'], purpose: 'Outgoing email' },
  { key: 'auth', title: 'Authentication', providers: ['google'], purpose: 'Customer sign-in' },
]

interface ProviderMeta {
  icon?: LucideIcon
  tone?: string
  image?: string
}

const PROVIDER_META: Record<string, ProviderMeta> = {
  billplz: { image: '/integrations/billplz.svg' },
  chip: { image: '/integrations/chip.svg' },
  sendscape: { image: '/integrations/sendscape.svg' },
  google: { image: '/integrations/google.svg' },
  poslaju: { image: '/integrations/poslaju.svg' },
}

const FALLBACK_META = { icon: PlugZap, tone: 'border-foreground/30 bg-foreground/10 text-foreground/70' }

const pill = (active: boolean) =>
  cn(
    'rounded-lg border px-3.5 py-2 text-sm font-semibold',
    active ? 'border-primary/60 bg-primary/20 text-primary' : 'border-line text-muted hover:bg-foreground/5',
  )

export default function AdminIntegrations() {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [params, setParams] = useSearchParams()
  const [managing, setManaging] = useState<string | null>(null)
  // Switching the gateway moves every new top-up, so it asks first.
  const [switchTo, setSwitchTo] = useState<{ driver: string; label: string } | null>(null)

  const integrations = useQuery({
    queryKey: ['admin-integrations'],
    queryFn: async () =>
      (await api.get<{ data: Provider[]; google_redirect_uri: string; poslaju_webhook_url: string }>('/admin/integrations')).data,
  })

  const setDriver = useMutation({
    mutationFn: async (driver: string) =>
      (await api.patch('/admin/integrations/payments', { driver })).data,
    onSuccess: (res: { message?: string }) => {
      queryClient.invalidateQueries({ queryKey: ['admin-integrations'] })
      setSwitchTo(null)
      toast.success(res.message ?? 'Payment gateway updated')
    },
    onError: (err) => {
      setSwitchTo(null)
      toast.error('Update failed', errorMessage(err))
    },
  })

  if (integrations.isLoading) return <LogoLoader />

  const providers = integrations.data?.data ?? []
  const byKey = new Map(providers.map((p) => [p.provider, p]))
  const routing = byKey.get('payments')
  const activeDriver = routing?.fields.find((f) => f.key === 'driver')?.value ?? 'mock'
  const grouped = new Set(GROUPS.flatMap((g) => g.providers))
  const others = providers.filter((p) => p.provider !== 'payments' && !grouped.has(p.provider))
  const managingProvider = managing ? byKey.get(managing) : undefined

  const sections = [
    ...GROUPS.map((g) => ({
      ...g,
      rows: g.providers.map((key) => byKey.get(key)).filter((p): p is Provider => p !== undefined),
    })),
    { key: 'other', title: 'Other', providers: [], purpose: '', rows: others },
  ].filter((s) => s.rows.length > 0)

  const section = sections.find((s) => s.key === params.get('tab')) ?? sections[0]

  return (
    <div>
      <PageHeader
        title="Integrations"
        subtitle="Credentials for payments, sign-in and email. Saved here rather than in the deployment environment, so they can be changed without a redeploy."
      />

      <div className="mb-5 flex flex-wrap gap-1.5">
        {sections.map((s) => (
          <button
            key={s.key}
            type="button"
            className={pill(s.key === section?.key)}
            onClick={() => setParams(s.key === sections[0].key ? {} : { tab: s.key }, { replace: true })}
          >
            {s.title}
          </button>
        ))}
      </div>

      {section?.key === 'payments' && (
        <p className="mb-4 text-xs text-foreground/60">
          Only one payment gateway can be active at a time. Setting one active switches all new top-ups to it; the
          others keep their saved keys.
        </p>
      )}

      {section && (
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          {section.rows.map((provider) => {
            const isGateway = section.key === 'payments'
            const isActive = isGateway && activeDriver === provider.provider
            return (
              <ProviderCard
                key={provider.provider}
                provider={provider}
                purpose={section.purpose}
                isActive={isActive}
                onManage={() => setManaging(provider.provider)}
                onSetActive={
                  isGateway && !isActive
                    ? () => setSwitchTo({ driver: provider.provider, label: provider.label })
                    : undefined
                }
              />
            )
          })}
          {section.key === 'payments' && (
            <MockGatewayCard
              isActive={activeDriver === 'mock'}
              onSetActive={() => setSwitchTo({ driver: 'mock', label: 'Mock gateway' })}
            />
          )}
        </div>
      )}

      {managingProvider && (
        <ManageProviderModal
          provider={managingProvider}
          redirectUri={integrations.data?.google_redirect_uri ?? ''}
          webhookUrl={integrations.data?.poslaju_webhook_url ?? ''}
          onClose={() => setManaging(null)}
        />
      )}

      <ConfirmDialog
        open={!!switchTo}
        onClose={() => setSwitchTo(null)}
        onConfirm={() => switchTo && setDriver.mutate(switchTo.driver)}
        title={switchTo ? `Switch payments to ${switchTo.label}?` : ''}
        confirmLabel="Switch gateway"
        variant={switchTo?.driver === 'mock' ? 'danger' : 'primary'}
        loading={setDriver.isPending}
      >
        {switchTo?.driver === 'mock'
          ? 'Every new wallet top-up will be credited instantly without charging anyone. Use this only for testing, never on the live shop.'
          : `Every new wallet top-up will go through ${switchTo?.label}. The current gateway keeps its saved keys, so you can switch back.`}
      </ConfirmDialog>
    </div>
  )
}

/** The card frame both card kinds share: logo and status on top, text, then a footer row. */
function IntegrationCard({
  logo,
  title,
  blurb,
  status,
  footer,
  actions,
}: {
  logo: ReactNode
  title: string
  blurb: string
  status: ReactNode
  footer: ReactNode
  actions: ReactNode
}) {
  return (
    <Card className="flex h-full flex-col">
      <div className="flex items-start justify-between gap-3">
        {logo}
        {status}
      </div>
      <div className="mt-3 text-sm font-medium text-foreground">{title}</div>
      <p className="mt-1 line-clamp-3 flex-1 text-xs text-foreground/60" title={blurb}>
        {blurb}
      </p>
      <div className="mt-4 flex items-center justify-between gap-2 border-t border-foreground/10 pt-4">
        <span className="min-w-0 truncate text-xs text-foreground/60">{footer}</span>
        <div className="flex min-h-10 shrink-0 gap-2">{actions}</div>
      </div>
    </Card>
  )
}

/* Sandbox and mock are said in the warning colour: neither charges anyone. */
function ModeLabel({ mode }: { mode: string }) {
  if (mode === 'live') {
    return (
      <span className="inline-flex items-center gap-1.5">
        <Radio className="size-3.5 text-success" /> Live mode
      </span>
    )
  }

  return (
    <span className="inline-flex items-center gap-1.5 font-medium text-warning">
      <FlaskConical className="size-3.5" /> {mode === 'mock' ? 'Mock - testing only' : 'Sandbox mode'}
    </span>
  )
}

function MockGatewayCard({ isActive, onSetActive }: { isActive: boolean; onSetActive: () => void }) {
  return (
    <IntegrationCard
      logo={
        <span className="flex size-10 shrink-0 items-center justify-center rounded-full border border-warning/60 bg-warning/20 text-warning">
          <FlaskConical className="size-5" />
        </span>
      }
      title="Mock gateway"
      blurb="Built-in test gateway. Credits top-ups instantly without contacting a real payment provider."
      status={isActive ? <Badge tone="brand">Active</Badge> : null}
      footer={<ModeLabel mode="mock" />}
      actions={
        !isActive && (
          <Button variant="outline" className="h-9 px-3 text-xs" onClick={onSetActive}>
            Set active
          </Button>
        )
      }
    />
  )
}

function ProviderCard({
  provider,
  purpose,
  isActive,
  onManage,
  onSetActive,
}: {
  provider: Provider
  purpose: string
  isActive: boolean
  onManage: () => void
  /** Only on a payment gateway that is not the active one. */
  onSetActive?: () => void
}) {
  const meta = PROVIDER_META[provider.provider] ?? FALLBACK_META
  const Icon = meta.icon ?? PlugZap
  const mode = provider.fields.find((f) => f.key === 'mode')?.value

  // One pill: Active outranks Configured, which outranks Incomplete.
  const status = isActive ? (
    <Badge tone="brand">Active</Badge>
  ) : provider.configured ? (
    <Badge tone="success">
      <CheckCircle2 className="size-3.5" /> Configured
    </Badge>
  ) : (
    <Badge tone="neutral">
      <XCircle className="size-3.5" /> Incomplete
    </Badge>
  )

  return (
    <IntegrationCard
      logo={
        meta.image ? (
          <img src={meta.image} alt="" className="size-10 shrink-0" />
        ) : (
          <span className={cn('flex size-10 shrink-0 items-center justify-center rounded-full border', meta.tone)}>
            <Icon className="size-5" />
          </span>
        )
      }
      title={provider.label}
      blurb={provider.blurb}
      status={status}
      footer={mode ? <ModeLabel mode={mode} /> : purpose}
      actions={
        <>
          {onSetActive && (
            <Button variant="outline" className="h-9 px-3 text-xs" onClick={onSetActive}>
              Set active
            </Button>
          )}
          <Button variant="outline" className="h-9 px-3 text-xs" onClick={onManage}>
            Manage
          </Button>
        </>
      }
    />
  )
}

function ManageProviderModal({
  provider,
  redirectUri,
  webhookUrl,
  onClose,
}: {
  provider: Provider
  redirectUri: string
  webhookUrl: string
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  // Only the fields the admin actually touched are held here, so a blank
  // secret input stays blank on submit and the backend keeps the stored one.
  const [edits, setEdits] = useState<Record<string, string>>({})

  const save = useMutation({
    mutationFn: async () => {
      const payload: Record<string, string> = {}
      for (const field of provider.fields) {
        const edited = edits[field.key]
        if (edited !== undefined) {
          payload[field.key] = edited
        } else if (field.type !== 'secret') {
          payload[field.key] = field.value ?? ''
        }
      }
      return (await api.patch(`/admin/integrations/${provider.provider}`, payload)).data
    },
    onSuccess: (res: { message?: string }) => {
      queryClient.invalidateQueries({ queryKey: ['admin-integrations'] })
      toast.success(res.message ?? 'Saved')
      onClose()
    },
    onError: (err) => toast.error('Save failed', errorMessage(err)),
  })

  const test = useMutation({
    mutationFn: async () => (await api.post(`/admin/integrations/${provider.provider}/test`)).data,
    onSuccess: (res: { message?: string }) => toast.success('Connection checked', res.message),
    onError: (err) => toast.error('Check failed', errorMessage(err)),
  })

  const setField = (key: string, value: string) => setEdits((prev) => ({ ...prev, [key]: value }))

  const valueOf = (field: IntegrationField) =>
    edits[field.key] ?? (field.type === 'secret' ? '' : (field.value ?? ''))

  return (
    <Modal
      open
      onClose={onClose}
      title={`Manage ${provider.label}`}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="outline" loading={test.isPending} onClick={() => test.mutate()}>
            <PlugZap className="size-4" /> Test connection
          </Button>
          <Button loading={save.isPending} onClick={() => save.mutate()}>
            Save
          </Button>
        </>
      }
    >
      <div className="max-h-[65vh] space-y-4 overflow-y-auto pr-1">
        <p className="text-xs text-foreground/60">{provider.blurb}</p>

        {provider.fields.map((field) => (
          <Field key={field.key} label={field.label} htmlFor={`${provider.provider}-${field.key}`}>
            {field.type === 'bool' ? (
              <Select
                id={`${provider.provider}-${field.key}`}
                value={valueOf(field) === '1' ? '1' : '0'}
                onChange={(v) => setField(field.key, v)}
                options={[
                  { value: '0', label: 'Off' },
                  { value: '1', label: 'On' },
                ]}
              />
            ) : field.type === 'select' ? (
              <Select
                id={`${provider.provider}-${field.key}`}
                value={valueOf(field)}
                onChange={(v) => setField(field.key, v)}
                placeholder="Select..."
                options={(field.options ?? []).map((option) => ({ value: option, label: option }))}
              />
            ) : (
              <Input
                id={`${provider.provider}-${field.key}`}
                type={field.type === 'secret' ? 'password' : 'text'}
                autoComplete="off"
                value={valueOf(field)}
                placeholder={field.hint ?? ''}
                onChange={(e) => setField(field.key, e.target.value)}
              />
            )}
            {field.type === 'secret' && field.has_value && (
              <p className="mt-1 text-xs text-foreground/50">
                Stored as {field.hint}. Leave blank to keep it.
              </p>
            )}
          </Field>
        ))}

        {provider.provider === 'google' && (
          <div className="rounded-lg border border-line bg-foreground/5 p-3">
            <p className="text-xs font-medium text-foreground">Authorised redirect URI</p>
            <p className="mt-1 break-all font-mono text-xs text-foreground/70">{redirectUri}</p>
            <Button
              variant="ghost"
              className="mt-1.5 px-2 text-xs"
              onClick={() => {
                navigator.clipboard.writeText(redirectUri)
                toast.success('Copied')
              }}
            >
              <Copy className="size-3.5" /> Copy
            </Button>
            <p className="mt-1 text-xs text-foreground/50">
              Paste this into the Google Cloud console verbatim. A mismatch is rejected before sign-in
              starts.
            </p>
          </div>
        )}

        {provider.provider === 'poslaju' && (
          <div className="rounded-lg border border-line bg-foreground/5 p-3">
            <p className="text-xs font-medium text-foreground">Webhook (callback) URL</p>
            <p className="mt-1 break-all font-mono text-xs text-foreground/70">{webhookUrl}</p>
            <Button
              variant="ghost"
              className="mt-1.5 px-2 text-xs"
              onClick={() => {
                navigator.clipboard.writeText(webhookUrl)
                toast.success('Copied')
              }}
            >
              <Copy className="size-3.5" /> Copy
            </Button>
            <p className="mt-1 text-xs text-foreground/50">
              Paste this into SendParcel Pro under Integration &gt; Webhook, with the same header name and secret as
              above and the subscription code you create there. The Test All Events button on that page then proves
              the link with no API credentials needed.
            </p>
          </div>
        )}
      </div>
    </Modal>
  )
}
